// src/context/AuthContext.js

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { CircularProgress, Box, Snackbar, Alert } from '@mui/material';

const AuthContext = createContext();

// Session configuration
const INACTIVITY_TIMEOUT = 20 * 60 * 1000; // 20 minutes in milliseconds
const MAX_SESSION_DURATION = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
const WARNING_BEFORE_LOGOUT = 2 * 60 * 1000; // 2 minutes warning before auto-logout

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [role, setRole] = useState('customer'); 
  const [loading, setLoading] = useState(true);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Track user activity
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    setShowWarning(false);
  }, []);

  // Logout function must be defined before useEffect
  const performLogout = useCallback(() => {
    signOut(auth);
  }, []);

  // Check for inactivity and session expiration
  useEffect(() => {
    if (!currentUser) return;

    const checkSession = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivity;
      const sessionDuration = sessionStartTime ? now - sessionStartTime : 0;

      // Check if session exceeded maximum duration
      if (sessionDuration > MAX_SESSION_DURATION) {
        performLogout();
        alert('Your session has expired after 4 hours. Please log in again.');
        return;
      }

      // Check for inactivity
      if (timeSinceActivity > INACTIVITY_TIMEOUT) {
        performLogout();
        alert('You have been logged out due to inactivity.');
        return;
      }

      // Show warning before auto-logout
      if (timeSinceActivity > INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT) {
        setShowWarning(true);
      }
    };

    const interval = setInterval(checkSession, 30000); // Check every 30 seconds

    // Add event listeners for user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => window.addEventListener(event, updateActivity));

    return () => {
      clearInterval(interval);
      events.forEach(event => window.removeEventListener(event, updateActivity));
    };
  }, [currentUser, lastActivity, sessionStartTime, updateActivity, performLogout]);

  // Save session start time
  const saveSessionStart = async (userId) => {
    try {
      const sessionRef = doc(db, 'sessions', userId);
      await setDoc(sessionRef, {
        startTime: serverTimestamp(),
        lastActivity: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      // Error logging removed for security
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCurrentUser(null);
        setRole('customer');
        setLoading(false);
        setSessionStartTime(null);
        return;
      }

      // Set session start time
      const startTime = Date.now();
      setSessionStartTime(startTime);
      setLastActivity(startTime);
      
      // Save session to Firestore
      await saveSessionStart(user.uid);

      // Get user role
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const r = data.role?.toLowerCase() === 'admin' ? 'admin' : 'customer';
        setRole(r);
      } else {
        setRole('customer');
      }
      
      setCurrentUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  async function logout() {
    try {
      // Clear session data
      if (currentUser) {
        const sessionRef = doc(db, 'sessions', currentUser.uid);
        await setDoc(sessionRef, {
          endTime: serverTimestamp(),
          lastActivity: serverTimestamp()
        }, { merge: true });
      }
    } catch (error) {
      // Error logging removed for security
    }
    
    return signOut(auth);
  }

  // Extend session when user is active after warning
  const extendSession = () => {
    setLastActivity(Date.now());
    setShowWarning(false);
  };

  const contextValue = {
    currentUser,
    role,
    logout,
    loading,
    extendSession,
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'var(--bg-primary)',
          color: 'var(--text-primary)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: 'var(--primary-blue)' }} />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      
      {/* Inactivity Warning */}
      <Snackbar
        open={showWarning}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity="warning" 
          sx={{ width: '100%' }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <button onClick={extendSession} style={{ cursor: 'pointer' }}>
                Continue
              </button>
              <button onClick={performLogout} style={{ cursor: 'pointer' }}>
                Logout
              </button>
            </Box>
          }
        >
          You will be logged out in 2 minutes due to inactivity
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
}
